/**
 * Unified price calculation system
 */
export class PriceCalculator {
  constructor() {
    this.elements = {
      totalAdded: document.querySelector('[data-total-added-price]'),
      totalExtras: document.querySelector('[data-total-extras-price]'),
      totalPrice: document.querySelector('[data-total-price]'),
      basePrice: document.querySelector('[data-base-price]'),
      totalAddedFront: document.querySelector('[data-total-added-price-front]'),
      totalPriceFront: document.querySelector('[data-total-price-front]')
    };

    this.basePrice = this.parsePrice(this.elements.basePrice?.textContent) || 0;
    this.observers = [];
    
    this.init();
  }

  init() {
    // Set up mutation observers for automatic price updates
    if (this.elements.totalAdded) {
      this.observeElement(this.elements.totalAdded);
    }
    if (this.elements.totalExtras) {
      this.observeElement(this.elements.totalExtras);
    }

    // Initial calculation
    this.updateTotalPrice();
  }

  observeElement(element) {
    const observer = new MutationObserver(() => {
      this.updateTotalPrice();
    });
    
    observer.observe(element, {
      childList: true,
      characterData: true,
      subtree: true
    });

    this.observers.push(observer);
  }

  parsePrice(priceText) {
    if (!priceText) return 0;
    return parseFloat(priceText.replace(/\s/g, '')) || 0;
  }

  formatPrice(price) {
    return price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  /**
   * Calculate total price from selected items
   * @param {string} buttonSelector - Selector for selected buttons
   * @param {string} itemSelector - Selector for item containers  
   * @param {string} priceAttribute - Data attribute containing price
   * @returns {number} Total price
   */
  calculateSelectedItemsPrice(buttonSelector = '.select-item-button.selected', itemSelector = '.single-interior-item', priceAttribute = 'data-interior-item-price') {
    const selectedButtons = document.querySelectorAll(buttonSelector);
    let totalPrice = 0;

    selectedButtons.forEach(button => {
      const parentItem = button.closest(itemSelector);
      const priceSpan = parentItem?.querySelector(`[${priceAttribute}]`);
      const numericPrice = parseFloat(priceSpan?.dataset.priceValue || '0');
      
      if (!isNaN(numericPrice)) {
        totalPrice += numericPrice;
      }
    });

    return totalPrice;
  }

  /**
   * Update the total added price display
   * @param {number} price - New price to display
   */
  updateAddedPrice(price) {
    if (this.elements.totalAdded) {
      this.elements.totalAdded.textContent = this.formatPrice(price);
    }
  }

  /**
   * Update the total extras price display
   * @param {number} price - New price to display  
   */
  updateExtrasPrice(price) {
    if (this.elements.totalExtras) {
      this.elements.totalExtras.textContent = this.formatPrice(price);
    }
  }

  /**
   * Recalculate and update the main total price
   */
  updateTotalPrice() {
    const addedPrice = this.parsePrice(this.elements.totalAdded?.textContent);
    const extrasPrice = this.parsePrice(this.elements.totalExtras?.textContent);
    const totalPrice = this.basePrice + addedPrice + extrasPrice;

    if (this.elements.totalPrice) {
      this.elements.totalPrice.textContent = this.formatPrice(totalPrice);
    }

    // Sync to front elements
    this.syncFrontValues();
  }

  /**
   * Sync main price values to their front display elements
   */
  syncFrontValues() {
    if (this.elements.totalPrice && this.elements.totalPriceFront) {
      this.elements.totalPriceFront.textContent = this.elements.totalPrice.textContent;
    }

    if (this.elements.totalAdded && this.elements.totalAddedFront) {
      this.elements.totalAddedFront.textContent = this.elements.totalAdded.textContent;
    }
  }

  /**
   * Update prices based on plan selection for interior items
   * @param {string} planName - Selected plan name
   * @param {Function} updateItemsCallback - Callback to update item prices in backend
   */
  updatePricesForPlan(planName, updateItemsCallback) {
    const interiorItems = document.querySelectorAll('.single-interior-item');
    const updatedItems = [];
    let totalPrice = 0;

    interiorItems.forEach(item => {
      const priceSpan = item.querySelector('[data-interior-item-price]');
      const selectButton = item.querySelector('.select-item-button');

      if (priceSpan && selectButton) {
        let priceValue;
        let isPriceTbd = false;

        if (planName) {
          // Regular apartment with plan selection (A/B/C)
          priceValue = item.getAttribute(`data-plan-price-${planName.toLowerCase()}`);
          isPriceTbd = item.getAttribute(`data-plan-price-tbd-${planName.toLowerCase()}`) === "1";
        } else {
          // Resident apartment with single price - use 'hind'
          priceValue = item.getAttribute(`data-plan-price-hind`);
          isPriceTbd = item.getAttribute("data-plan-price-tbd-hind") === "1";
        }

        if (priceValue !== null) {
          const numericPrice = parseFloat(priceValue) || 0;

          // Update display
          priceSpan.dataset.priceValue = isPriceTbd ? "0" : String(numericPrice);
          if (isPriceTbd) {
            priceSpan.textContent = "Hind täpsustub";
          } else {
            priceSpan.textContent = numericPrice === 0 ? "0 €" : `${this.formatPrice(numericPrice)} €`;
          }

          // Track for backend update
          const itemDataJson = selectButton.getAttribute('data-item');
          if (itemDataJson) {
            try {
              const itemData = JSON.parse(itemDataJson);
              itemData.price = isPriceTbd ? 0 : numericPrice;
              itemData.price_tbd = isPriceTbd;
              updatedItems.push(itemData);

              // Add to total if selected
              if (selectButton.classList.contains('selected')) {
                totalPrice += itemData.price;
              }
            } catch (e) {
              console.error('Invalid item data:', e);
            }
          }
        }
      }
    });

    // Update display - preserve current extras price
    const extrasTotal = this.parsePrice(this.elements.totalExtras?.textContent) || 0;
    const totalAddedPrice = totalPrice + extrasTotal;
    this.updateAddedPrice(totalAddedPrice);

    // Update backend if callback provided
    if (updateItemsCallback && updatedItems.length > 0) {
      updateItemsCallback(updatedItems);
    }
  }

  /**
   * Clean up observers
   */
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}
